import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import {
  COLORS,
  CONSTANTS,
  FONTSIZE,
  FONTWEIGHT
} from "helpers/designConstants";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  rectSortingStrategy,
  arrayMove,
  useSortable
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { firebaseRandomID } from "helpers/helperFunctions";
import { MiniButton } from "components/shared/standardElements";
import ArticleCard from "components/shared/ArticleCard";
import useArticleZoomModal from "components/shared/articleZoomModal/useArticleZoomModal";
import ArticleZoomModal from "components/shared/articleZoomModal/ArticleZoomModal";
import useInventoryModal from "components/shared/inventoryModal/useInventoryModal";
import InventoryModal from "components/shared/inventoryModal/InventoryModal";
import ConfirmModal from "components/shared/ConfirmModal";
import EditSectionNameModal from "./EditSectionNameModal";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SortIcon from "@mui/icons-material/Sort";
import UndoIcon from "@mui/icons-material/Undo";
import FolderIcon from "@mui/icons-material/Folder";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import useOnClickOutside from "helpers/useOnClickOutside";
import {
  updateSectionById,
  removeSectionById,
  findSectionById,
  countArticlesDeep,
  collectAllArticleIDs,
  collectLeafSections,
  sanitizeSections
} from "helpers/sectionTreeHelpers";
import { cloneSectionTree } from "helpers/cloneSectionTree";
import { useDatabaseReader } from "database/useDatabaseReader";
import TemplateCataloguePicker from "../TemplateCataloguePicker";

// --- Recursive helpers for deep nesting ---

const flattenSections = (sections, expandedIds, parentId = null, depth = 0) => {
  const items = [];
  sections.forEach((section) => {
    const hasChildren = section.groups && section.groups.length > 0;
    items.push({
      id: section.id,
      type: hasChildren ? "parent" : "leaf",
      parentId,
      section,
      depth
    });
    if (hasChildren && expandedIds.has(section.id)) {
      items.push(
        ...flattenSections(section.groups, expandedIds, section.id, depth + 1)
      );
    }
  });
  return items;
};

// --- Sort helper for articles by sortMode ---
const sortArticles = (articles, sortMode, articleIDs) => {
  if (!articles || articles.length === 0) return articles;
  switch (sortMode) {
    case "name_asc":
      return [...articles].sort((a, b) =>
        (a.articleName || "").localeCompare(b.articleName || "")
      );
    case "price_desc":
      return [...articles].sort(
        (a, b) => (b.retailPrice || 0) - (a.retailPrice || 0)
      );
    case "price_asc":
      return [...articles].sort(
        (a, b) => (a.retailPrice || 0) - (b.retailPrice || 0)
      );
    case "custom": {
      // Preserve articleIDs array order
      if (!articleIDs) return articles;
      const idxMap = new Map(articleIDs.map((id, i) => [id, i]));
      return [...articles].sort(
        (a, b) => (idxMap.get(a.id) ?? Infinity) - (idxMap.get(b.id) ?? Infinity)
      );
    }
    case "default":
    default:
      return [...articles].sort(
        (a, b) => (b.retailPrice || 0) - (a.retailPrice || 0)
      );
  }
};

// Helper: get max relative depth of a subtree (for DnD depth cap)
const getSubtreeMaxRelativeDepth = (itemId, flatItems) => {
  const startItem = flatItems.find((i) => i.id === itemId);
  if (!startItem) return 0;
  const startDepth = startItem.depth;
  let maxRel = 0;
  const collectDesc = (parentId) => {
    flatItems.forEach((item) => {
      if (item.parentId === parentId) {
        maxRel = Math.max(maxRel, item.depth - startDepth);
        collectDesc(item.id);
      }
    });
  };
  collectDesc(itemId);
  return maxRel;
};

// --- Sortable Sidebar Item ---
const SortableSidebarItem = ({
  item,
  isSelected,
  onSelect,
  onEdit,
  onDelete,
  onAddSub,
  onIndent,
  onOutdent,
  canIndent,
  canOutdent,
  isExpanded,
  onToggleExpand,
  isDragActive,
  language
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging
  } = useSortable({ id: item.id, animateLayoutChanges: () => false });

  const style = {
    opacity: isDragging ? 0.5 : 1,
    pointerEvents: isDragging ? "none" : undefined
  };

  const { t } = useTranslation();
  const name = language === "fr" ? (item.section.name_fr || item.section.name) : item.section.name;

  const articleCount = countArticlesDeep(item.section);
  const [showOverflow, setShowOverflow] = useState(false);
  const overflowRef = useRef(null);
  useOnClickOutside(overflowRef, () => setShowOverflow(false));
  return (
    <ItemRow
      ref={setNodeRef}
      style={style}
      depth={item.depth}
      isLeaf={item.type !== "parent"}
      isSelected={isSelected}
      onClick={() =>
        item.type === "parent"
          ? onToggleExpand(item.id)
          : onSelect(item.section)
      }
    >
      <DragHandle {...attributes} {...listeners}>
        <DragIndicatorIcon style={{ fontSize: 18 }} />
      </DragHandle>
      <ExpandChevron
        isLeaf={item.type !== "parent"}
        onClick={
          item.type === "parent"
            ? (e) => {
                e.stopPropagation();
                onToggleExpand(item.id);
              }
            : undefined
        }
      >
        {item.type === "parent" &&
          (isExpanded ? (
            <ArrowDropDownIcon style={{ fontSize: 20 }} />
          ) : (
            <ArrowRightIcon style={{ fontSize: 20 }} />
          ))}
      </ExpandChevron>
      <ItemLabel
        isSelected={isSelected}
        depth={item.depth}
        isParent={item.type === "parent"}
        title={name}
      >
        {item.type === "parent" && (
          <FolderIcon style={{ fontSize: 14, opacity: 0.5, flexShrink: 0 }} />
        )}
        {name}
        {item.type !== "parent" && <ItemCount>({articleCount})</ItemCount>}
      </ItemLabel>
      <HoverActions $isSelected={isSelected}>
        {item.depth < 3 && (
          <SmallIconButton
            onClick={(e) => {
              e.stopPropagation();
              onAddSub(item.id);
            }}
            title={t("add_sub_group") || "Add Sub-Group"}
          >
            <AddIcon style={{ fontSize: 14 }} />
          </SmallIconButton>
        )}
        <OverflowMenuContainer ref={overflowRef}>
          <SmallIconButton
            onClick={(e) => {
              e.stopPropagation();
              setShowOverflow(!showOverflow);
            }}
            title={t("more_actions") || "More actions"}
          >
            <MoreVertIcon style={{ fontSize: 14 }} />
          </SmallIconButton>
          {showOverflow && (
            <OverflowDropdown>
              <OverflowItem onClick={(e) => { e.stopPropagation(); onEdit(item.section); setShowOverflow(false); }}>
                <EditIcon style={{ fontSize: 14 }} /> {t("rename") || "Rename"}
              </OverflowItem>
              {canOutdent && (
                <OverflowItem onClick={(e) => { e.stopPropagation(); onOutdent(item.id); setShowOverflow(false); }}>
                  <ArrowBackIcon style={{ fontSize: 14 }} /> {t("move_out") || "Move Out"}
                </OverflowItem>
              )}
              {canIndent && (
                <OverflowItem onClick={(e) => { e.stopPropagation(); onIndent(item.id); setShowOverflow(false); }}>
                  <ArrowForwardIcon style={{ fontSize: 14 }} /> {t("move_into_group_above") || "Move Into Group Above"}
                </OverflowItem>
              )}
              <OverflowItem
                style={{ color: COLORS.alertRed }}
                onClick={(e) => { e.stopPropagation(); onDelete(item.section); setShowOverflow(false); }}
              >
                <DeleteIcon style={{ fontSize: 14 }} /> {t("delete_button") || "Delete"}
              </OverflowItem>
            </OverflowDropdown>
          )}
        </OverflowMenuContainer>
      </HoverActions>
    </ItemRow>
  );
};

// --- Sortable Article Card (for Custom Order DnD) ---
const SortableArticleCard = ({
  article, catalogue, seasonMap, onZoomArticle, onViewInventory,
  isSelected, onToggleSelect
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: article.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1
  };

  return (
    <SelectableCardWrapper
      ref={setNodeRef}
      style={style}
      isSelected={isSelected}
      onClick={(e) => onToggleSelect(article.id, e)}
      {...attributes}
      {...listeners}
    >
      <DragIndicatorOverlay>
        <DragIndicatorIcon style={{ fontSize: 16 }} />
      </DragIndicatorOverlay>
      <SelectionCheckbox checked={isSelected} />
      <ArticleCard
        article={article}
        seasonKey={seasonMap?.get(article.id) || catalogue?.seasonKey}
        showInventoryButton
        shouldShowLimitedSoldOut
        priceToShow={catalogue?.priceToShowPreview}
        onZoomArticle={onZoomArticle}
        onViewInventory={onViewInventory}
      />
    </SelectableCardWrapper>
  );
};

// --- Sort Mode Dropdown ---
const SORT_OPTION_KEYS = ["default", "name_asc", "price_asc", "custom"];

const SortModeDropdown = ({ currentMode, onSelect }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);
  useOnClickOutside(ref, () => setIsOpen(false));

  const sortLabels = {
    default: t("sort_price_high_low") || "Price (High→Low)",
    name_asc: t("sort_name_az") || "Name A→Z",
    price_asc: t("sort_price_low_high") || "Price Low→High",
    custom: t("custom_order") || "Custom Order"
  };

  const active = currentMode || "default";

  return (
    <SortDropdownContainer ref={ref}>
      <SortTrigger onClick={() => setIsOpen(!isOpen)} title={t("sort_articles") || "Sort articles"}>
        <SortIcon style={{ fontSize: 18 }} />
        <SortLabel>{sortLabels[active]}</SortLabel>
      </SortTrigger>
      <SortNav isActive={isOpen}>
        <SortNavHeader>{t("sort") || "Sort"}</SortNavHeader>
        <ul>
          {SORT_OPTION_KEYS.map((key) => (
            <SortNavItem
              key={key}
              isSelected={key === active}
              onClick={() => {
                onSelect(key);
                setIsOpen(false);
              }}
            >
              {sortLabels[key]}
            </SortNavItem>
          ))}
        </ul>
      </SortNav>
    </SortDropdownContainer>
  );
};

// --- Floating Action Bar (shared between custom-order and non-custom) ---
const FloatingActionBar = ({
  selectedCount,
  onRemove,
  showMoveToDropdown,
  onToggleMoveToDropdown,
  moveToRef,
  leafSections,
  onMoveToSection,
  t
}) => (
  <FloatingBar>
    <span>{selectedCount} {t("selected") || "selected"}</span>
    <FloatingBarActions>
      <RemoveButton onClick={onRemove}>
        {t("remove_from_catalogue") || "Remove from Catalogue"}
      </RemoveButton>
      <MoveToWrapper ref={moveToRef}>
        <MiniButton onClick={onToggleMoveToDropdown}>
          {t("move_to") || "Move To..."}
        </MiniButton>
        {showMoveToDropdown && (
          <MoveToDropdown>
            {leafSections.length > 0 ? (
              leafSections.map((leaf) => (
                <MoveToOption
                  key={leaf.id}
                  onClick={() => onMoveToSection(leaf.id)}
                >
                  {leaf.parentPath.length > 0 && (
                    <BreadcrumbPrefix>{leaf.parentPath.join(" → ")} → </BreadcrumbPrefix>
                  )}{leaf.name}
                </MoveToOption>
              ))
            ) : (
              <MoveToEmpty>{t("no_groups_available") || "No other groups available"}</MoveToEmpty>
            )}
          </MoveToDropdown>
        )}
      </MoveToWrapper>
    </FloatingBarActions>
  </FloatingBar>
);

// --- Main Component ---
const CatalogueSectionBuilder = ({
  catalogue,
  customSections,
  onUpdateSections,
  allArticles = [],
  seasonMap,
  onOpenProductPicker,
  onBack,
  onRefreshArticles,
  onRemoveArticlesFromCatalogue,
  catalogues = []
}) => {
  const { t, i18n } = useTranslation();
  const { getCatalogueByID } = useDatabaseReader();
  const [showImportPicker, setShowImportPicker] = useState(false);
  const importPickerRef = useRef(null);
  useOnClickOutside(importPickerRef, () => setShowImportPicker(false));

  const handleImportFromCatalogue = useCallback(
    async (sourceCatalogue) => {
      setShowImportPicker(false);
      const source = await getCatalogueByID(sourceCatalogue.id);
      if (!source?.customSections?.length) return;
      const cloned = cloneSectionTree(source.customSections, { clearArticleIDs: true });
      onUpdateSections(sanitizeSections(cloned));
    },
    [getCatalogueByID, onUpdateSections]
  );

  // Undo history for sidebar changes
  const sectionsHistoryRef = useRef([]);
  const [undoCount, setUndoCount] = useState(0);
  const MAX_UNDO_HISTORY = 20;

  // Wrap onUpdateSections to strip undefined values before Firestore save
  const safeUpdateSections = useCallback(
    (sections) => {
      sectionsHistoryRef.current = [
        ...sectionsHistoryRef.current.slice(-(MAX_UNDO_HISTORY - 1)),
        customSections
      ];
      setUndoCount(sectionsHistoryRef.current.length);
      onUpdateSections(sanitizeSections(sections));
    },
    [onUpdateSections, customSections]
  );

  const undoSections = useCallback(() => {
    if (sectionsHistoryRef.current.length === 0) return;
    const prev = sectionsHistoryRef.current[sectionsHistoryRef.current.length - 1];
    sectionsHistoryRef.current = sectionsHistoryRef.current.slice(0, -1);
    setUndoCount(sectionsHistoryRef.current.length);
    onUpdateSections(sanitizeSections(prev));
  }, [onUpdateSections]);
  const {
    toggleArticleZoomModal,
    isZoomModalShowing,
    onZoomArticle,
    zoomArticle,
    zoomArticleImages
  } = useArticleZoomModal();
  const {
    isInventoryModalShowing,
    toggleInventoryModal,
    inventoryArticle,
    onViewInventory
  } = useInventoryModal();

  // State
  const [selectedSection, setSelectedSection] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [deletingSection, setDeletingSection] = useState(null);
  const [selectedArticleIDs, setSelectedArticleIDs] = useState(new Set());
  const [showMoveToDropdown, setShowMoveToDropdown] = useState(false);
  const [articleSearchTerm, setArticleSearchTerm] = useState("");
  const lastClickedIndexRef = useRef(null);
  const moveToRef = useRef(null);
  useOnClickOutside(moveToRef, () => setShowMoveToDropdown(false));
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [activeItem, setActiveItem] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const dragStartPointer = useRef({ x: 0 });
  const sidebarScrollRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 }
    })
  );

  // Once articles load, strip stale articleIDs not present in allArticles
  // (handles articles removed while in schema mode)
  const hasCleanedStaleIds = useRef(false);
  useEffect(() => {
    if (hasCleanedStaleIds.current) return;
    if (!allArticles.length || !customSections.length) return;
    hasCleanedStaleIds.current = true;
    const validIds = new Set(allArticles.map((a) => a.id));
    let dirty = false;
    const clean = (sections) =>
      sections.map((s) => {
        if (s.groups) return { ...s, groups: clean(s.groups) };
        if (!s.articleIDs) return s;
        const filtered = s.articleIDs.filter((id) => validIds.has(id));
        if (filtered.length !== s.articleIDs.length) {
          dirty = true;
          return { ...s, articleIDs: filtered };
        }
        return s;
      });
    const cleaned = clean(customSections);
    if (dirty) safeUpdateSections(cleaned);
  }, [allArticles, customSections, safeUpdateSections]);

  // Ctrl+Z / Cmd+Z keyboard shortcut for undo
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "z") {
        e.preventDefault();
        undoSections();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [undoSections]);

  // Always expand all parent nodes — runs on mount and when sections change
  // (e.g. after importing from a template)
  useEffect(() => {
    const parentIds = new Set();
    const traverse = (sections) =>
      sections.forEach((s) => {
        if (s.groups && s.groups.length > 0) {
          parentIds.add(s.id);
          traverse(s.groups);
        }
      });
    traverse(customSections);
    if (parentIds.size > 0) {
      setExpandedIds((prev) => {
        const merged = new Set([...prev, ...parentIds]);
        if (merged.size === prev.size) return prev;
        return merged;
      });
    }
  }, [customSections]);

  const toggleExpand = useCallback((id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const flatItems = useMemo(
    () => flattenSections(customSections, expandedIds),
    [customSections, expandedIds]
  );

  // --- Section CRUD ---
  const addSection = useCallback(
    (parentId = null) => {
      // Don't create yet — just open the naming dialog with isNew flag
      setEditingSection({ isNew: true, parentId, name: "" });
    },
    []
  );

  const saveSection = useCallback(
    (name) => {
      if (!editingSection) return;

      if (editingSection.isNew) {
        // Create the node now that the user confirmed a name
        const newSection = {
          id: firebaseRandomID(),
          name,
          name_fr: name,
          articleIDs: []
        };

        if (editingSection.parentId == null) {
          safeUpdateSections([...customSections, newSection]);
        } else {
          const updated = updateSectionById(
            customSections,
            editingSection.parentId,
            (section) => {
              if (!section.groups || section.groups.length === 0) {
                newSection.articleIDs = section.articleIDs || [];
                const { articleIDs: _removed, ...parent } = section;
                return { ...parent, groups: [newSection] };
              }
              return { ...section, groups: [...section.groups, newSection] };
            }
          );
          setExpandedIds((prev) => new Set([...prev, editingSection.parentId]));
          safeUpdateSections(updated);
        }
      } else {
        // Rename existing node
        const updated = updateSectionById(customSections, editingSection.id, (s) => ({
          ...s,
          name,
          name_fr: name
        }));
        safeUpdateSections(updated);
      }
      setEditingSection(null);
    },
    [editingSection, customSections, safeUpdateSections]
  );

  const deleteSection = useCallback(
    (sectionId) => {
      safeUpdateSections(removeSectionById(customSections, sectionId));
      if (selectedSection?.id === sectionId) setSelectedSection(null);
      setDeletingSection(null);
    },
    [customSections, safeUpdateSections, selectedSection]
  );

  // --- Unified DND Handler ---
  const getProjectedItems = useCallback(
    (activeId) => {
      // Build set of active item + all its descendants
      const excludeIds = new Set([activeId]);
      const collectDesc = (parentId) => {
        flatItems.forEach((item) => {
          if (item.parentId === parentId) {
            excludeIds.add(item.id);
            collectDesc(item.id);
          }
        });
      };
      collectDesc(activeId);
      return flatItems.filter((i) => !excludeIds.has(i.id));
    },
    [flatItems]
  );

  const computeDropTarget = useCallback(
    (activeId, overId, pointerX, isAboveMidpoint) => {
      const projected = getProjectedItems(activeId);
      const overIdx = projected.findIndex((i) => i.id === overId);
      if (overIdx === -1) return null;

      const overItem = projected[overIdx];
      const subtreeMaxDepth = getSubtreeMaxRelativeDepth(activeId, flatItems);

      // When pointer is above the midpoint of the over item, insert BEFORE it
      if (isAboveMidpoint) {
        if (overIdx === 0) {
          // Before the very first item — insert at same level
          return {
            beforeId: overId,
            depth: overItem.depth,
            targetParentId: overItem.parentId,
            insertAfterId: "__before_first__"
          };
        }
        // Use previous item as anchor (insert after it = before over)
        const prevItem = projected[overIdx - 1];
        return {
          afterId: prevItem.id,
          depth: overItem.depth,
          targetParentId: overItem.parentId,
          insertAfterId: prevItem.parentId === overItem.parentId ? prevItem.id : "__before_first_child__"
        };
      }

      const itemBelow = projected[overIdx + 1];

      // Calculate intended depth from pointer X relative to sidebar
      const sidebarRect = sidebarScrollRef.current?.getBoundingClientRect();
      if (!sidebarRect) return null;
      const relativeX = pointerX - sidebarRect.left;
      const rawDepth = Math.max(0, Math.round((relativeX - 30) / 14));

      // Calculate valid depth range for the gap after overItem
      let maxDepth, minDepth;
      const overIsLeafWithArticles =
        !overItem.section.groups?.length && overItem.section.articleIDs?.length > 0;

      if (itemBelow && itemBelow.parentId === overItem.id) {
        // Gap between expanded parent and its first child — can only insert as child
        maxDepth = Math.min(overItem.depth + 1, 3 - subtreeMaxDepth);
        minDepth = Math.min(overItem.depth + 1, maxDepth);
      } else if (overIsLeafWithArticles) {
        // Leaf with articles — don't allow nesting into it
        maxDepth = Math.min(overItem.depth, 3 - subtreeMaxDepth);
        minDepth = itemBelow ? itemBelow.depth : 0;
      } else {
        maxDepth = Math.min(overItem.depth + 1, 3 - subtreeMaxDepth); // depth cap accounts for subtree
        minDepth = itemBelow ? itemBelow.depth : 0;
      }

      const depth = Math.max(minDepth, Math.min(maxDepth, rawDepth));

      // Determine target parent and insertion position
      let targetParentId, insertAfterId;

      if (depth === overItem.depth + 1) {
        // Nesting into overItem
        targetParentId = overItem.id;
        insertAfterId = null; // insert as first child
      } else if (depth === overItem.depth) {
        // Sibling of overItem — insert after it
        targetParentId = overItem.parentId;
        insertAfterId = overItem.id;
      } else {
        // Walk up from overItem to find ancestor at target depth
        let current = overItem;
        while (current && current.depth > depth) {
          const parent = projected.find((i) => i.id === current.parentId);
          if (!parent) break;
          current = parent;
        }
        targetParentId = current.parentId;
        insertAfterId = current.id;
      }

      return { afterId: overId, depth, targetParentId, insertAfterId };
    },
    [getProjectedItems, flatItems]
  );

  const handleDragStart = useCallback(
    (event) => {
      const item = flatItems.find((i) => i.id === event.active.id);
      setActiveItem(item || null);
      dragStartPointer.current.x = event.activatorEvent?.clientX || 0;
      dragStartPointer.current.y = event.activatorEvent?.clientY || 0;
    },
    [flatItems]
  );

  const handleDragMove = useCallback(
    (event) => {
      const { active, over, delta } = event;
      if (!over || active.id === over.id) {
        setDropTarget(null);
        return;
      }
      const pointerX = dragStartPointer.current.x + delta.x;
      const pointerY = dragStartPointer.current.y + delta.y;
      const overRect = over.rect;
      const isAboveMidpoint = overRect && pointerY < (overRect.top + overRect.bottom) / 2;
      const target = computeDropTarget(active.id, over.id, pointerX, isAboveMidpoint);
      setDropTarget(target);
    },
    [computeDropTarget]
  );

  // --- Indent/outdent (hierarchy changes) ---
  const indentSection = useCallback(
    (sectionId) => {
      // Find the section and its sibling above in the flat list
      const idx = flatItems.findIndex((i) => i.id === sectionId);
      if (idx <= 0) return;
      const item = flatItems[idx];
      // Find the nearest sibling above (same parentId)
      let targetItem = null;
      for (let i = idx - 1; i >= 0; i--) {
        if (flatItems[i].parentId === item.parentId) {
          targetItem = flatItems[i];
          break;
        }
      }
      if (!targetItem) return;
      // Don't allow indenting into a leaf that has articles
      const targetSection = targetItem.section;
      if (!targetSection.groups?.length && targetSection.articleIDs?.length > 0) return;
      const movedItem = item.section;
      let updated = removeSectionById(customSections, sectionId);
      updated = updateSectionById(updated, targetItem.id, (target) => {
        if (target.groups && target.groups.length > 0)
          return { ...target, groups: [...target.groups, movedItem] };
        // Convert leaf to parent — only preserve existing articles as a child
        // if the leaf actually had articles
        if (target.articleIDs && target.articleIDs.length > 0) {
          const existingChild = {
            id: firebaseRandomID(),
            name: target.name,
            name_fr: target.name_fr,
            articleIDs: target.articleIDs
          };
          const { articleIDs: _removed, ...parent } = target;
          return { ...parent, groups: [existingChild, movedItem] };
        }
        const { articleIDs: _removed, ...parent } = target;
        return { ...parent, groups: [movedItem] };
      });
      setExpandedIds((prev) => new Set([...prev, targetItem.id]));
      safeUpdateSections(updated);
    },
    [flatItems, customSections, safeUpdateSections]
  );

  const outdentSection = useCallback(
    (sectionId) => {
      const item = flatItems.find((i) => i.id === sectionId);
      if (!item || item.parentId === null) return;
      const parentItem = flatItems.find((i) => i.id === item.parentId);
      if (!parentItem) return;
      const movedItem = item.section;
      let updated = removeSectionById(customSections, sectionId);
      if (parentItem.parentId === null) {
        // Promote to top level, insert after parent
        const parentIdx = updated.findIndex((s) => s.id === parentItem.id);
        updated = [
          ...updated.slice(0, parentIdx + 1),
          movedItem,
          ...updated.slice(parentIdx + 1)
        ];
      } else {
        // Insert into grandparent's groups after parent
        updated = updateSectionById(updated, parentItem.parentId, (gp) => {
          if (!gp.groups) return gp;
          const pIdx = gp.groups.findIndex((s) => s.id === parentItem.id);
          const newGroups = [...gp.groups];
          newGroups.splice(pIdx + 1, 0, movedItem);
          return { ...gp, groups: newGroups };
        });
      }
      safeUpdateSections(updated);
    },
    [flatItems, customSections, safeUpdateSections]
  );

  const handleDragEnd = useCallback(
    (event) => {
      setActiveItem(null);
      setDropTarget(null);

      const { active, over, delta } = event;
      if (!over || active.id === over.id) return;

      const pointerX = dragStartPointer.current.x + delta.x;
      const pointerY = dragStartPointer.current.y + delta.y;
      const overRect = over.rect;
      const isAboveMidpoint = overRect && pointerY < (overRect.top + overRect.bottom) / 2;
      const target = computeDropTarget(active.id, over.id, pointerX, isAboveMidpoint);
      if (!target) return;

      const activeFlat = flatItems.find((i) => i.id === active.id);
      if (!activeFlat) return;

      const { targetParentId, insertAfterId } = target;
      const movedSection = activeFlat.section;

      // Remove from current position
      let updated = removeSectionById(customSections, active.id);

      // Insert at target position
      if (targetParentId === null) {
        // Insert at root level
        if (insertAfterId === null || insertAfterId === "__before_first__") {
          updated = [movedSection, ...updated];
        } else {
          const idx = updated.findIndex((s) => s.id === insertAfterId);
          if (idx === -1) {
            updated = [...updated, movedSection];
          } else {
            updated = [...updated.slice(0, idx + 1), movedSection, ...updated.slice(idx + 1)];
          }
        }
      } else {
        // Insert in a parent's children
        updated = updateSectionById(updated, targetParentId, (parent) => {
          if (parent.groups && parent.groups.length > 0) {
            if (insertAfterId === null || insertAfterId === "__before_first_child__") {
              return { ...parent, groups: [movedSection, ...parent.groups] };
            }
            const idx = parent.groups.findIndex((s) => s.id === insertAfterId);
            const newGroups = [...parent.groups];
            newGroups.splice(idx + 1, 0, movedSection);
            return { ...parent, groups: newGroups };
          }
          // Leaf → parent conversion
          if (parent.articleIDs && parent.articleIDs.length > 0) {
            const existingChild = {
              id: firebaseRandomID(),
              name: parent.name,
              name_fr: parent.name_fr,
              articleIDs: parent.articleIDs
            };
            const { articleIDs: _removed, ...parentData } = parent;
            return { ...parentData, groups: [existingChild, movedSection] };
          }
          const { articleIDs: _removed, ...parentData } = parent;
          return { ...parentData, groups: [movedSection] };
        });
        setExpandedIds((prev) => new Set([...prev, targetParentId]));
      }

      safeUpdateSections(updated);
    },
    [flatItems, customSections, safeUpdateSections, computeDropTarget]
  );

  // --- Computed values ---
  const assignedArticleIDs = collectAllArticleIDs(customSections);
  const unassignedArticles = allArticles.filter(
    (a) => !assignedArticleIDs.has(a.id)
  );

  // Auto-close Unassigned panel when it becomes empty
  useEffect(() => {
    if (selectedSection?.id === "__unassigned__" && unassignedArticles.length === 0) {
      setSelectedSection(null);
    }
  }, [unassignedArticles.length, selectedSection]);

  const getSelectedArticles = useCallback(() => {
    if (!selectedSection) return [];
    if (selectedSection.id === "__unassigned__") return unassignedArticles;
    const found = findSectionById(customSections, selectedSection.id);
    if (found && found.articleIDs) {
      const filtered = allArticles.filter((a) => found.articleIDs.includes(a.id));
      return sortArticles(filtered, found.sortMode, found.articleIDs);
    }
    return [];
  }, [selectedSection, customSections, allArticles, unassignedArticles]);

  const selectSection = useCallback((section) => {
    setSelectedSection(section);
    setSelectedArticleIDs(new Set());
    setShowMoveToDropdown(false);
    setArticleSearchTerm("");
    lastClickedIndexRef.current = null;
  }, []);

  const toggleArticleSelection = useCallback((articleId, event, filteredList) => {
    if (event?.shiftKey && lastClickedIndexRef.current != null && filteredList) {
      const currentIdx = filteredList.findIndex((a) => a.id === articleId);
      if (currentIdx !== -1) {
        const start = Math.min(lastClickedIndexRef.current, currentIdx);
        const end = Math.max(lastClickedIndexRef.current, currentIdx);
        setSelectedArticleIDs((prev) => {
          const next = new Set(prev);
          for (let i = start; i <= end; i++) {
            next.add(filteredList[i].id);
          }
          return next;
        });
        lastClickedIndexRef.current = currentIdx;
        return;
      }
    }
    const idx = filteredList ? filteredList.findIndex((a) => a.id === articleId) : null;
    if (idx != null && idx !== -1) lastClickedIndexRef.current = idx;
    setSelectedArticleIDs((prev) => {
      const next = new Set(prev);
      if (next.has(articleId)) {
        next.delete(articleId);
      } else {
        next.add(articleId);
      }
      return next;
    });
  }, []);

  const getAllLeafSections = () =>
    collectLeafSections(customSections).filter(
      (s) => s.id !== selectedSection?.id
    );

  const moveArticlesToSection = useCallback(
    (targetSectionId, articleIDsToMove = null) => {
      const idsToMove = new Set(articleIDsToMove || [...selectedArticleIDs]);
      const removeFromAll = (sections) =>
        sections.map((s) => ({
          ...s,
          articleIDs: s.articleIDs?.filter((id) => !idsToMove.has(id)),
          ...(s.groups ? { groups: removeFromAll(s.groups) } : {})
        }));
      let updated = removeFromAll(customSections);
      if (targetSectionId !== "__unassigned__") {
        updated = updateSectionById(updated, targetSectionId, (s) => ({
          ...s,
          articleIDs: [...(s.articleIDs || []), ...idsToMove]
        }));
      }
      safeUpdateSections(updated);
      setSelectedArticleIDs(new Set());
      setShowMoveToDropdown(false);
    },
    [selectedArticleIDs, customSections, safeUpdateSections]
  );

  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  const confirmRemoveSelectedArticles = useCallback(() => {
    if (!selectedSection || selectedArticleIDs.size === 0) return;
    // Always remove from catalogue entirely — Unassigned is not a destination
    if (onRemoveArticlesFromCatalogue) {
      onRemoveArticlesFromCatalogue([...selectedArticleIDs]);
    }
    setSelectedArticleIDs(new Set());
    setShowRemoveConfirm(false);
  }, [selectedSection, selectedArticleIDs, onRemoveArticlesFromCatalogue]);

  const handleSortModeChange = useCallback(
    (sectionId, sortMode) => {
      const updated = updateSectionById(customSections, sectionId, (s) => ({
        ...s,
        sortMode: sortMode === "default" ? undefined : sortMode
      }));
      safeUpdateSections(updated);
    },
    [customSections, safeUpdateSections]
  );

  // --- Article DnD for Custom Order ---
  const articleSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 }
    })
  );

  const handleArticleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id || !selectedSection) return;
      const found = findSectionById(customSections, selectedSection.id);
      if (!found?.articleIDs) return;
      const oldIndex = found.articleIDs.indexOf(active.id);
      const newIndex = found.articleIDs.indexOf(over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      const newOrder = arrayMove(found.articleIDs, oldIndex, newIndex);
      const updated = updateSectionById(customSections, selectedSection.id, (s) => ({
        ...s,
        articleIDs: newOrder
      }));
      safeUpdateSections(updated);
    },
    [customSections, selectedSection, safeUpdateSections]
  );

  // Get the live section for the selected section (for reading sortMode etc.)
  const liveSelectedSection = selectedSection?.id && selectedSection.id !== "__unassigned__"
    ? findSectionById(customSections, selectedSection.id)
    : null;
  const currentSortMode = liveSelectedSection?.sortMode || "default";
  const isLeafSelected = liveSelectedSection && !liveSelectedSection.groups?.length;
  const isCustomOrder = currentSortMode === "custom";
  const displayedArticles = getSelectedArticles();
  const filteredArticles = useMemo(() => {
    if (!articleSearchTerm.trim()) return displayedArticles;
    const term = articleSearchTerm.toLowerCase().trim();
    return displayedArticles.filter(
      (a) =>
        (a.articleName || "").toLowerCase().includes(term) ||
        (a.articleNumber || "").toLowerCase().includes(term)
    );
  }, [displayedArticles, articleSearchTerm]);

  const toggleSelectAll = useCallback(() => {
    const allIds = filteredArticles.map((a) => a.id);
    setSelectedArticleIDs((prev) => {
      const allSelected = allIds.every((id) => prev.has(id));
      if (allSelected) return new Set();
      return new Set(allIds);
    });
  }, [filteredArticles]);

  return (
    <PageWrapper>
      <PageHeader>
        <BackNav onClick={onBack}>
          <ArrowBackIosIcon style={{ width: "14px" }} />
          {t("back_to_catalogues") || "Back to Catalogues"}
        </BackNav>
        <PageHeaderCenter>
          <CatalogueName>{catalogue?.name}</CatalogueName>
          <MiniButton
            onClick={() => {
              const domain = window.location.hostname === "localhost"
                ? "http://localhost:3000"
                : `https://${window.location.hostname}`;
              window.open(`${domain}/catalogue/viewer?ctgid=${encodeURIComponent(catalogue?.id)}`, "_blank");
            }}
            title={t("preview_as_buyer") || "Preview as Buyer"}
          >
            {t("preview") || "Preview"}
          </MiniButton>
        </PageHeaderCenter>
        <MiniButton
          onClick={() => onOpenProductPicker(liveSelectedSection || selectedSection)}
          style={{
            visibility: isLeafSelected && selectedSection?.id !== "__unassigned__"
              ? "visible"
              : "hidden"
          }}
        >
          + {t("add_articles") || "Add Articles"}
        </MiniButton>
      </PageHeader>
      <ContentArea>
        {/* Left: Sidebar Tree Editor */}
        <Sidebar>
          <SidebarHeader>
            <SidebarTitle>
              {t("groups") || "Groups"}
            </SidebarTitle>
            <SidebarActions>
              <UndoButton
                onClick={undoSections}
                title={t("undo") || "Undo"}
                disabled={undoCount === 0}
                style={{ opacity: undoCount === 0 ? 0.3 : 1 }}
              >
                <UndoIcon style={{ fontSize: 14 }} />
              </UndoButton>
              <AddGroupButton onClick={() => addSection()}>
                <AddIcon style={{ fontSize: 14 }} />
                {t("add_group") || "Add Group"}
              </AddGroupButton>
            </SidebarActions>
          </SidebarHeader>
          <SidebarScroll ref={sidebarScrollRef}>
            <DndContext
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragMove={handleDragMove}
              onDragEnd={handleDragEnd}
              onDragCancel={() => { setActiveItem(null); setDropTarget(null); }}
              sensors={sensors}
            >
              <SortableContext
                items={flatItems.map((i) => i.id)}
                strategy={verticalListSortingStrategy}
              >
                {flatItems.map((item, idx) => (
                  <React.Fragment key={item.id}>
                    {dropTarget?.beforeId === item.id && activeItem && (
                      <DropIndicatorLine depth={dropTarget.depth} />
                    )}
                    <SortableSidebarItem
                      isDragActive={activeItem != null}
                      item={item}
                      isSelected={selectedSection?.id === item.section.id}
                      onSelect={selectSection}
                      onEdit={setEditingSection}
                      onDelete={setDeletingSection}
                      onAddSub={addSection}
                      onIndent={indentSection}
                      onOutdent={outdentSection}
                      language={i18n.language}
                      canIndent={
                        // Can indent if there's a sibling above to nest into, max depth 4
                        // and that sibling is not a leaf with articles
                        item.depth < 3 &&
                        idx > 0 &&
                        flatItems.slice(0, idx).some(
                          (prev) =>
                            prev.parentId === item.parentId &&
                            (prev.section.groups?.length > 0 || !prev.section.articleIDs?.length)
                        )
                      }
                      canOutdent={item.parentId !== null}
                      isExpanded={expandedIds.has(item.id)}
                      onToggleExpand={toggleExpand}
                    />
                    {dropTarget?.afterId === item.id && activeItem && (
                      <DropIndicatorLine depth={dropTarget.depth} />
                    )}
                  </React.Fragment>
                ))}
              </SortableContext>
              <DragOverlay dropAnimation={null} />
            </DndContext>

            {/* Empty state — import from existing catalogue */}
            {customSections.length === 0 && (
              <SidebarEmptyState ref={importPickerRef}>
                <SidebarEmptyBlurb>
                  {t("import_groups_blurb") || "Get started by adding groups, or import a structure from another catalogue."}
                </SidebarEmptyBlurb>
                <ImportButton onClick={() => setShowImportPicker((v) => !v)}>
                  {t("import_from_catalogue") || "Import from Catalogue"}
                </ImportButton>
                {showImportPicker && (
                  <ImportPickerPopover>
                    <TemplateCataloguePicker
                      catalogues={catalogues}
                      onSelect={handleImportFromCatalogue}
                      excludeID={catalogue?.id}
                    />
                  </ImportPickerPopover>
                )}
              </SidebarEmptyState>
            )}

            {/* Unassigned zone — only shown when there are unassigned articles */}
            {unassignedArticles.length > 0 && (
              <UnassignedZone
                isSelected={selectedSection?.id === "__unassigned__"}
                onClick={() =>
                  selectSection({
                    id: "__unassigned__",
                    name: "Unassigned",
                    name_fr: "Non Assigné"
                  })
                }
              >
                {t("unassigned") || "Unassigned"} ({unassignedArticles.length})
              </UnassignedZone>
            )}
          </SidebarScroll>
        </Sidebar>

        {/* Right: Article Panel */}
        <ArticlePanel>
          {selectedSection ? (
            <>
              <ArticlePanelHeader>
                <ArticlePanelHeaderLeft>
                  {liveSelectedSection?.name || selectedSection.name}
                  <ArticleSearchInput
                    type="text"
                    placeholder={t("search") || "Search..."}
                    value={articleSearchTerm}
                    onChange={(e) => setArticleSearchTerm(e.target.value)}
                  />
                </ArticlePanelHeaderLeft>
                <ArticlePanelHeaderRight>
                  {filteredArticles.length > 0 && (
                    <SelectAllToggle onClick={toggleSelectAll}>
                      {filteredArticles.every((a) => selectedArticleIDs.has(a.id))
                        ? (t("deselect_all") || "Deselect All")
                        : (t("select_all") || "Select All")}
                    </SelectAllToggle>
                  )}
                  {isLeafSelected && (
                    <SortModeDropdown
                      currentMode={currentSortMode}
                      onSelect={(mode) => handleSortModeChange(selectedSection.id, mode)}
                    />
                  )}
                </ArticlePanelHeaderRight>
              </ArticlePanelHeader>
              {filteredArticles.length === 0 && selectedSection?.id !== "__unassigned__" ? (
                <GroupEmptyState>
                  <span>{t("no_articles_in_group") || "No articles in this group yet."}</span>
                  <MiniButton onClick={() => onOpenProductPicker(liveSelectedSection || selectedSection)}>
                    + {t("add_articles") || "Add Articles"}
                  </MiniButton>
                </GroupEmptyState>
              ) : isCustomOrder ? (
                <>
                <CustomOrderBanner>
                  <DragIndicatorIcon style={{ fontSize: 16 }} />
                  {t("drag_to_reorder") || "Drag cards to reorder"}
                </CustomOrderBanner>
                <DndContext
                  collisionDetection={closestCenter}
                  onDragEnd={handleArticleDragEnd}
                  sensors={articleSensors}
                >
                  <SortableContext
                    items={filteredArticles.map((a) => a.id)}
                    strategy={rectSortingStrategy}
                  >
                    <ArticleGrid>
                      {filteredArticles.map((article) => (
                        <SortableArticleCard
                          key={article.id}
                          article={article}
                          catalogue={catalogue}
                          seasonMap={seasonMap}
                          isSelected={selectedArticleIDs.has(article.id)}
                          onToggleSelect={(id, e) => toggleArticleSelection(id, e, filteredArticles)}
                          onZoomArticle={onZoomArticle}
                          onViewInventory={onViewInventory}
                        />
                      ))}
                    </ArticleGrid>
                  </SortableContext>
                </DndContext>
                {selectedArticleIDs.size > 0 && (
                  <FloatingActionBar
                    selectedCount={selectedArticleIDs.size}
                    onRemove={() => setShowRemoveConfirm(true)}
                    showMoveToDropdown={showMoveToDropdown}
                    onToggleMoveToDropdown={() => setShowMoveToDropdown(!showMoveToDropdown)}
                    moveToRef={moveToRef}
                    leafSections={getAllLeafSections()}
                    onMoveToSection={moveArticlesToSection}
                    t={t}
                  />
                )}
                </>
              ) : (
                <>
                  <ArticleGrid>
                    {filteredArticles.map((article) => (
                      <SelectableCardWrapper
                        key={article.id}
                        isSelected={selectedArticleIDs.has(article.id)}
                        onClick={(e) => toggleArticleSelection(article.id, e, filteredArticles)}
                      >
                        <SelectionCheckbox checked={selectedArticleIDs.has(article.id)} />
                        <ArticleCard
                          article={article}
                          seasonKey={seasonMap?.get(article.id) || catalogue?.seasonKey}
                          showInventoryButton
                          shouldShowLimitedSoldOut
                          priceToShow={catalogue?.priceToShowPreview}
                          onZoomArticle={onZoomArticle}
                          onViewInventory={onViewInventory}
                        />
                      </SelectableCardWrapper>
                    ))}
                  </ArticleGrid>
                  {selectedArticleIDs.size > 0 && (
                    <FloatingActionBar
                      selectedCount={selectedArticleIDs.size}
                      onRemove={() => setShowRemoveConfirm(true)}
                      showMoveToDropdown={showMoveToDropdown}
                      onToggleMoveToDropdown={() => setShowMoveToDropdown(!showMoveToDropdown)}
                      moveToRef={moveToRef}
                      leafSections={getAllLeafSections()}
                      onMoveToSection={moveArticlesToSection}
                      t={t}
                    />
                  )}
                </>
              )}
            </>
          ) : (
            <EmptyState>
              {t("select_section_to_view_articles") ||
                "Select a group to view its articles"}
            </EmptyState>
          )}
        </ArticlePanel>
      </ContentArea>

      {/* Modals */}
      <EditSectionNameModal
        show={editingSection != null}
        onClose={() => setEditingSection(null)}
        currentName={editingSection?.name}
        onSave={saveSection}
        isNew={editingSection?.isNew}
      />
      <ConfirmModal
        show={deletingSection != null}
        onClose={() => setDeletingSection(null)}
        title={t("delete_section") || "Delete Group"}
        message={(() => {
          const base = t("confirm_section_delete", { name: deletingSection?.name }) ||
            `Are you sure you want to delete "${deletingSection?.name}"?`;
          const articleCount = deletingSection ? countArticlesDeep(deletingSection) : 0;
          if (articleCount > 0) {
            const warning = t("group_has_articles_warning", { count: articleCount }) ||
              `This group contains ${articleCount} article(s) that will become unassigned.`;
            return `${base} ${warning}`;
          }
          return base;
        })()}
        isDestructive
        buttonTitle={t("delete_button") || "Delete"}
        onConfirm={() => deleteSection(deletingSection?.id)}
      />
      <ArticleZoomModal
        show={isZoomModalShowing}
        onClose={toggleArticleZoomModal}
        imageList={zoomArticleImages}
        article={zoomArticle}
      />
      <InventoryModal
        show={isInventoryModalShowing}
        onClose={toggleInventoryModal}
        article={inventoryArticle}
      />
      <ConfirmModal
        show={showRemoveConfirm}
        onClose={() => setShowRemoveConfirm(false)}
        title={t("remove_from_catalogue") || "Remove from Catalogue"}
        message={t("confirm_remove_articles", { count: selectedArticleIDs.size }) || `Remove ${selectedArticleIDs.size} article(s) from this catalogue? They can be re-added later.`}
        isDestructive
        buttonTitle={t("remove") || "Remove"}
        onConfirm={confirmRemoveSelectedArticles}
      />
    </PageWrapper>
  );
};

// --- Styled Components ---

const PageWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const PageHeader = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  padding: 10px;
  border-bottom: 2px solid ${COLORS.secondary};
  background: ${COLORS.tertiary};
`;

const BackNav = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  color: ${COLORS.text};
  font-size: ${FONTSIZE.xsmall};
  font-weight: ${FONTWEIGHT.heavy};
  text-transform: uppercase;
  transition: 0.3s;

  &:hover {
    color: ${COLORS.primaryHover};
  }
`;

const PageHeaderCenter = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CatalogueName = styled.div`
  font-weight: ${FONTWEIGHT.heavy};
  font-size: ${FONTSIZE.medium};
  text-transform: uppercase;
  color: ${COLORS.text};
`;

const ContentArea = styled.div`
  display: flex;
  flex-grow: 1;
  min-height: 0;
  overflow: hidden;
`;

const Sidebar = styled.div`
  flex-shrink: 0;
  width: 340px;
  display: flex;
  flex-direction: column;
  border-left: 1px solid ${COLORS.secondary};
  border-right: 1px solid ${COLORS.secondary};
  border-bottom: 1px solid ${COLORS.secondary};
  border-bottom-left-radius: ${CONSTANTS.borderRadius};
  background: white;
  overflow: hidden;
`;

const SidebarHeader = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  padding: 0 10px;
  height: 50px;
  box-sizing: border-box;
  background: white;
  border-bottom: 1px solid ${COLORS.secondary};
`;

const SidebarTitle = styled.div`
  font-weight: ${FONTWEIGHT.heavy};
  font-size: ${FONTSIZE.medium};
  text-transform: uppercase;
  color: ${COLORS.text};
`;

const SidebarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const UndoButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: none;
  border-radius: ${CONSTANTS.borderRadius};
  background: transparent;
  color: ${COLORS.text};
  cursor: pointer;

  &:hover {
    background: ${COLORS.secondary};
    color: ${COLORS.primary};
  }
`;

const AddGroupButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px 8px;
  border: none;
  border-radius: ${CONSTANTS.borderRadius};
  background: transparent;
  color: ${COLORS.text};
  font-size: ${FONTSIZE.xsmall};
  font-weight: ${FONTWEIGHT.heavy};
  text-transform: uppercase;
  cursor: pointer;

  &:hover {
    background: ${COLORS.secondary};
    color: ${COLORS.primary};
  }
`;

const SidebarEmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 30px 20px;
  position: relative;
`;

const SidebarEmptyBlurb = styled.div`
  color: ${COLORS.text};
  font-size: ${FONTSIZE.xsmall};
  text-align: center;
  line-height: 1.4;
`;

const ImportButton = styled.button`
  padding: 8px 16px;
  border: 1px solid ${COLORS.secondary};
  border-radius: ${CONSTANTS.borderRadius};
  background: white;
  color: ${COLORS.text};
  font-size: ${FONTSIZE.xsmall};
  font-weight: ${FONTWEIGHT.heavy};
  text-transform: uppercase;
  cursor: pointer;

  &:hover {
    border-color: ${COLORS.primary};
    color: ${COLORS.primary};
  }
`;

const ImportPickerPopover = styled.div`
  position: absolute;
  top: 100%;
  left: 20px;
  right: 20px;
  margin-top: 4px;
  background: white;
  border: 1px solid ${COLORS.secondary};
  border-radius: ${CONSTANTS.borderRadius};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  padding: 10px;
`;

const SidebarScroll = styled.div`
  flex-grow: 1;
  overflow-y: auto;
  padding: 4px 0;
`;

// --- New sidebar item styled components ---

const ItemRow = styled.div`
  display: flex;
  align-items: center;
  padding: ${({ depth }) => (depth || 0) === 0 ? "8px 8px" : "6px 8px"};
  margin-left: ${({ depth, isLeaf }) => (depth || 0) * 16 + (isLeaf ? 18 : 0)}px;
  margin-right: 4px;
  border-radius: ${CONSTANTS.borderRadius};
  cursor: pointer;
  position: relative;
  background: ${({ isSelected }) =>
    isSelected ? COLORS.tertiary : "transparent"};

  @media (hover: hover) {
    &:hover {
      background: ${({ isSelected }) =>
        isSelected ? COLORS.tertiary : "#f8f8f8"};
    }
  }

  /* Parents: always reveal actions on hover (including touch-hover) so buttons stay discoverable */
  ${({ isLeaf }) => !isLeaf && `
    &:hover > [data-drag-handle] {
      opacity: 1;
    }
    &:hover > [data-hover-actions] {
      opacity: 1;
    }
  `}

  /* Leaves: only reveal on true hover (prevents iPad double-tap) */
  ${({ isLeaf }) => isLeaf && `
    @media (hover: hover) {
      &:hover > [data-drag-handle] {
        opacity: 1;
      }
      &:hover > [data-hover-actions] {
        opacity: 1;
      }
    }
  `}
`;

const DragHandle = styled.div.attrs({ "data-drag-handle": true })`
  cursor: grab;
  touch-action: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLORS.secondary};
  width: 20px;
  min-width: 20px;
  opacity: 0.3;
  transition: opacity 0.15s;

  @media (hover: hover) {
    &:hover {
      color: ${COLORS.text};
      opacity: 1;
    }
  }
`;

const ExpandChevron = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ isLeaf }) => (isLeaf ? "default" : "pointer")};
  color: ${COLORS.secondary};
  width: ${({ isLeaf }) => (isLeaf ? "0px" : "16px")};
  min-width: ${({ isLeaf }) => (isLeaf ? "0px" : "16px")};
  height: 20px;
  flex-shrink: 0;
  overflow: hidden;

  @media (hover: hover) {
    &:hover {
      color: ${({ isLeaf }) => (isLeaf ? COLORS.secondary : COLORS.text)};
    }
  }
`;

const ItemLabel = styled.div`
  flex-grow: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px;
  font-size: ${({ depth }) => (depth || 0) === 0 ? FONTSIZE.small : FONTSIZE.xsmall};
  font-weight: ${({ isParent }) => isParent ? FONTWEIGHT.heavy : FONTWEIGHT.medium};
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: ${COLORS.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemCount = styled.span`
  color: ${COLORS.secondary};
  font-size: ${FONTSIZE.xsmall};
  font-weight: ${FONTWEIGHT.medium};
  flex-shrink: 0;
`;

const HoverActions = styled.div.attrs({ "data-hover-actions": true })`
  display: flex;
  gap: 0px;
  opacity: ${({ $isSelected }) => ($isSelected ? 1 : 0)};
  transition: opacity 0.15s;
  flex-shrink: 0;
`;

const OverflowMenuContainer = styled.div`
  position: relative;
`;

const OverflowDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  min-width: 180px;
  background: white;
  border: 1px solid ${COLORS.secondary};
  border-radius: ${CONSTANTS.borderRadius};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  overflow: hidden;
`;

const OverflowItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: ${FONTSIZE.xsmall};
  font-weight: ${FONTWEIGHT.heavy};
  text-transform: uppercase;
  color: ${COLORS.text};

  @media (hover: hover) {
    &:hover {
      background: ${COLORS.tertiary};
      color: ${COLORS.primary};
    }
  }

  &:not(:last-child) {
    border-bottom: 1px solid ${COLORS.tertiary};
  }
`;

const SmallIconButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 3px;
  cursor: pointer;
  color: ${COLORS.text};

  @media (hover: hover) {
    &:hover {
      background: ${({ isDestructive }) =>
        isDestructive ? COLORS.alertRed : COLORS.secondary};
      color: ${({ isDestructive }) =>
        isDestructive ? "white" : COLORS.primary};
    }
  }
`;

const DropIndicatorLine = styled.div`
  height: 2px;
  background: ${COLORS.primaryHover};
  margin-left: ${({ depth }) => (depth || 0) === 0 ? 8 : 44 + (depth || 0) * 14}px;
  margin-right: 8px;
  border-radius: 1px;
  pointer-events: none;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    left: -3px;
    top: -3px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${COLORS.primaryHover};
  }
`;

const UnassignedZone = styled.div`
  margin: 8px 8px 4px;
  padding: 8px 10px;
  border-radius: ${CONSTANTS.borderRadius};
  color: ${COLORS.alertOrange};
  font-weight: ${FONTWEIGHT.heavy};
  font-size: ${FONTSIZE.xsmall};
  text-transform: uppercase;
  cursor: pointer;
  background: ${({ isSelected }) =>
    isSelected ? COLORS.tertiary : "transparent"};

  @media (hover: hover) {
    &:hover {
      background: ${({ isSelected }) =>
        isSelected ? COLORS.tertiary : "#f8f8f8"};
    }
  }
`;

const ArticlePanel = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: ${COLORS.tertiary};
`;

const ArticlePanelHeader = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  padding: 0 10px;
  height: 50px;
  box-sizing: border-box;
  background: ${COLORS.tertiary};
  border-bottom: 1px solid ${COLORS.secondary};
  font-weight: ${FONTWEIGHT.heavy};
  font-size: ${FONTSIZE.medium};
  text-transform: uppercase;
  color: ${COLORS.text};
  gap: 10px;
`;

const ArticlePanelHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 1;
  min-width: 0;
`;

const ArticlePanelHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const ArticleSearchInput = styled.input`
  padding: 4px 8px;
  height: 30px;
  box-sizing: border-box;
  border: 1px solid ${COLORS.secondary};
  border-radius: ${CONSTANTS.borderRadius};
  background: white;
  color: ${COLORS.text};
  font-size: ${FONTSIZE.xsmall};
  font-weight: ${FONTWEIGHT.medium};
  text-transform: none;
  width: 160px;
  outline: none;

  &:focus {
    border-color: ${COLORS.primary};
  }

  &::placeholder {
    color: ${COLORS.secondary};
    text-transform: uppercase;
  }
`;

const SelectAllToggle = styled.button`
  padding: 4px 10px;
  height: 30px;
  box-sizing: border-box;
  border: 1px solid ${COLORS.secondary};
  border-radius: ${CONSTANTS.borderRadius};
  background: white;
  color: ${COLORS.text};
  font-size: ${FONTSIZE.xsmall};
  font-weight: ${FONTWEIGHT.heavy};
  text-transform: uppercase;
  cursor: pointer;
  white-space: nowrap;

  @media (hover: hover) {
    &:hover {
      background: ${COLORS.tertiary};
    }
  }
`;

const SortDropdownContainer = styled.div`
  position: relative;
`;

const SortTrigger = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  height: 30px;
  box-sizing: border-box;
  border: 1px solid ${COLORS.secondary};
  border-radius: ${CONSTANTS.borderRadius};
  background: white;
  color: ${COLORS.text};
  font-size: ${FONTSIZE.xsmall};
  font-weight: ${FONTWEIGHT.heavy};
  text-transform: uppercase;
  cursor: pointer;
  transition: 0.2s ease;

  &:hover {
    background: ${COLORS.tertiary};
  }
`;

const SortLabel = styled.span`
  white-space: nowrap;
`;

const SortNav = styled.nav`
  text-align: center;
  background: #ffffff;
  border-radius: ${CONSTANTS.borderRadius};
  position: absolute;
  z-index: 1000;
  top: 38px;
  right: 0;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.3);
  white-space: nowrap;
  overflow: hidden;
  opacity: ${({ isActive }) => (isActive ? 1 : 0)};
  visibility: ${({ isActive }) => (isActive ? "visible" : "hidden")};
  transform: ${({ isActive }) =>
    isActive ? "translateY(0)" : "translateY(-10px)"};
  transition: opacity 0.25s ease, transform 0.25s ease, visibility 0.25s;

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }
`;

const SortNavHeader = styled.div`
  padding: 4px 10px;
  text-transform: uppercase;
  background-color: ${COLORS.text};
  font-weight: ${FONTWEIGHT.heavy};
  color: white;
  font-size: ${FONTSIZE.xsmall};
`;

const SortNavItem = styled.li`
  padding: 10px 20px;
  margin: 3px;
  border-radius: ${CONSTANTS.borderRadius};
  cursor: pointer;
  color: ${COLORS.text};
  font-weight: ${FONTWEIGHT.heavy};
  font-size: ${FONTSIZE.xsmall};
  text-transform: uppercase;
  background-color: ${({ isSelected }) =>
    isSelected ? COLORS.secondary : "inherit"};

  &:hover {
    background-color: ${COLORS.tertiary};
  }
`;

const ArticleGrid = styled.div`
  flex-grow: 1;
  overflow-y: auto;
  padding: 5px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 0.33333fr));
  gap: 5px;
  align-content: start;
`;

const SelectableCardWrapper = styled.div`
  position: relative;
  cursor: pointer;
  display: flex;
  border: 2px solid ${({ isSelected }) => isSelected ? COLORS.select : "transparent"};
  border-radius: ${CONSTANTS.borderRadius};
  transition: border-color 0.15s;
`;

const DragIndicatorOverlay = styled.div`
  position: absolute;
  top: 6px;
  left: 6px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLORS.secondary};
  z-index: 1;
  pointer-events: none;
  opacity: 0.6;
`;

const CustomOrderBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  color: ${COLORS.secondary};
  font-size: 10px;
  font-weight: ${FONTWEIGHT.medium};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const SelectionCheckbox = styled.div`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 20px;
  height: 20px;
  border: 2px solid ${({ checked }) => checked ? COLORS.select : COLORS.secondary};
  border-radius: 3px;
  background: ${({ checked }) => checked ? COLORS.select : "white"};
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;

  &::after {
    content: "${({ checked }) => checked ? "\\2713" : ""}";
    color: white;
    font-size: 14px;
    font-weight: bold;
  }
`;

const FloatingBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  background: ${COLORS.primaryHover};
  color: white;
  font-weight: ${FONTWEIGHT.heavy};
  font-size: ${FONTSIZE.small};
  text-transform: uppercase;
`;

const FloatingBarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RemoveButton = styled.button`
  padding: 6px 12px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: ${CONSTANTS.borderRadius};
  background: transparent;
  color: white;
  font-size: ${FONTSIZE.xsmall};
  font-weight: ${FONTWEIGHT.heavy};
  text-transform: uppercase;
  cursor: pointer;

  &:hover {
    background: ${COLORS.alertRed};
    border-color: ${COLORS.alertRed};
  }
`;

const MoveToWrapper = styled.div`
  position: relative;
`;

const MoveToDropdown = styled.div`
  position: absolute;
  bottom: 100%;
  right: 0;
  min-width: 200px;
  background: white;
  border: 1px solid ${COLORS.secondary};
  border-radius: ${CONSTANTS.borderRadius};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 10;
  max-height: 250px;
  overflow-y: auto;
  margin-bottom: 4px;
`;

const BreadcrumbPrefix = styled.span`
  font-weight: ${FONTWEIGHT.medium};
`;

const MoveToEmpty = styled.div`
  padding: 10px 14px;
  font-size: ${FONTSIZE.xsmall};
  font-weight: ${FONTWEIGHT.medium};
  text-transform: uppercase;
  color: ${COLORS.secondary};
  font-style: italic;
`;

const MoveToOption = styled.div`
  padding: 10px 14px;
  cursor: pointer;
  font-size: ${FONTSIZE.xsmall};
  font-weight: ${FONTWEIGHT.heavy};
  text-transform: uppercase;
  color: ${COLORS.text};

  &:hover {
    background: ${COLORS.tertiary};
    color: ${COLORS.primary};
  }

  &:not(:last-child) {
    border-bottom: 1px solid ${COLORS.tertiary};
  }
`;

const GroupEmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  height: 100%;
  flex-grow: 1;
  color: ${COLORS.text};
  font-size: ${FONTSIZE.small};
  text-transform: uppercase;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${COLORS.text};
  font-size: ${FONTSIZE.medium};
  text-transform: uppercase;
`;

export default CatalogueSectionBuilder;
